import { createSelector } from 'reselect'
import { GET_SUBSCRIPTION_LIST } from '~/shared/store/common/actionTypes'
import { selectActionList } from '~/shared/store/common/selects'
import {
  selectSelectedSubscription,
  selectSubscriptionList,
} from '~/shared/store/subscription/selects'
import { IAppState } from '~/shared/store/types'
import { GET_STATUS } from '~/shared/store/user/actionTypes'

export const selectIsDirectStripePaymentEnabled = (state: IAppState): boolean =>
  state.payment.isDirectStripePaymentEnabled

const withDirectStripeOverride = (state: IAppState, value: boolean): boolean =>
  selectIsDirectStripePaymentEnabled(state) ? false : value

// PRIMER
export const selectPrimerClientToken = (state: IAppState): string =>
  state.payment.primer.clientToken
export const selectIsPrimerCreditCardAvailable = (state: IAppState): boolean =>
  withDirectStripeOverride(state, state.payment.primer.isCreditCardAvailable)
export const selectIsPrimerRetryProcessing = (state: IAppState): boolean =>
  state.payment.primer.isRetryProcessing
export const selectIsPrimerFirstPaymentRetryPassed = (
  state: IAppState,
): boolean => state.payment.primer.isFirstPaymentRetryPassed
export const selectIsPrimerApplePayEnabled = (state: IAppState): boolean =>
  withDirectStripeOverride(state, state.payment.primer.applePayEnabled)
export const selectIsPrimerGooglePayEnabled = (state: IAppState): boolean =>
  withDirectStripeOverride(state, state.payment.primer.googlePayEnabled)

// STRIPE
export const select3DSecureIframeUrl = (state: IAppState): string =>
  state.payment.stripe.threeDSecureIframeURL
export const selectStripeClientSecret = (state: IAppState): string =>
  state.payment.stripe.clientSecret
export const selectStripeQuickPayMethod = (state: IAppState) =>
  state.payment.stripe.quickPayMethod
export const selectIsStripeQuickPayChecked = (state: IAppState): boolean =>
  state.payment.stripe.isQuickPayChecked
export const selectStripeAccountId = (state: IAppState): string =>
  state.payment.stripe.accountId
export const selectStripeAccountName = (state: IAppState): string =>
  state.payment.stripe.accountName
export const selectStripePublicKey = (state: IAppState): string =>
  state.payment.stripe.publicKey

// COMMON
export const selectIsSubscriptionPurchaseInitiated = (state: IAppState) =>
  state.payment.isSubscriptionPurchaseInitiated
export const selectIsAlreadyBought = (state: IAppState): boolean =>
  state.payment.isAlreadyBought
export const selectIsForce3DS = (state: IAppState): boolean =>
  state.payment.primer.isForce3DS
export const selectPrimerProcessorName = (state: IAppState): string =>
  state.payment.primer.processorName
export const selectPrimerApplePayProcessorName = (state: IAppState): string =>
  state.payment.primer.applePayProcessorName
export const selectPrimerGooglePayProcessorName = (state: IAppState): string =>
  state.payment.primer.googlePayProcessorName
export const selectPrimerPaypalProcessorName = (state: IAppState): string =>
  state.payment.primer.paypalProcessorName

// MEMORIZED
export const selectIsStripeConfigReady = createSelector(
  [selectStripeAccountId, selectStripeAccountName, selectStripePublicKey],
  (accountId, accountName, publicKey) =>
    [accountId, accountName, publicKey].every(Boolean),
)
export const selectAreSubscriptionsReady = createSelector(
  [selectActionList, selectSubscriptionList, selectSelectedSubscription],
  (fetchingActionsList, subscriptionsList, selectedSubscription) => {
    const hasPrices =
      !fetchingActionsList?.includes(GET_SUBSCRIPTION_LIST) &&
      !fetchingActionsList?.includes(GET_STATUS) &&
      !!subscriptionsList.length

    return hasPrices && !!selectedSubscription?.id
  },
)
export const selectIsPaymentInfoReady = createSelector(
  [selectIsStripeConfigReady, selectAreSubscriptionsReady],
  (isStripeConfigReady, areSubscriptionsReady) =>
    isStripeConfigReady && areSubscriptionsReady,
)
